import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ConfirmationNumber as TicketIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { passkitAPI } from '../../services/api';
import ResponsivePageHeader from '../../components/Common/ResponsivePageHeader';
import EventCreationDialog from '../../components/Events/EventCreationDialog';
import TicketTypeCreationDialog from '../../components/Events/TicketTypeCreationDialog';
import EventCalendarView from '../../components/Events/EventCalendarView';
import AddEventDialog from '../../components/Events/AddEventDialog';
import TicketTypeEditDialog from '../../components/Events/TicketTypeEditDialog';
import TicketTypeDeleteDialog from '../../components/Events/TicketTypeDeleteDialog';
import IssueTicketDialog from '../../components/EventTickets/IssueTicketDialog';
import { logger } from '../../utils/logger';

interface Production {
  id: string;
  name: string;
  description?: string;
  status: string;
  passkit_production_id: string;
  company_id: string;
}

interface TicketType {
  id: string;
  name: string;
  description?: string;
  externalId?: string;
  status?: string;
  productionId: string;
  passkit_template_id: string;  // This is beforeRedeemPassTemplateId
  afterRedeemPassTemplateId?: string;
  after_redeem_template_id?: string;
  production_name?: string;
  created_at?: string;
  updated_at?: string;
  events?: Array<{
    id?: string;
    name?: string;
    event_date: string;
    venue_name?: string;
  }>;
}

const Events: React.FC = () => {
  // Data states
  const [productions, setProductions] = useState<Production[]>([]);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [events, setEvents] = useState<Array<{
    id: string;
    name: string;
    event_date: string;
    venue_name?: string;
    venue_id?: string;
  }>>([]);
  const [selectedProduction, setSelectedProduction] = useState<Production | null>(null);

  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingTicketTypes, setLoadingTicketTypes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [ticketTypeDialogOpen, setTicketTypeDialogOpen] = useState(false);
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTicketType, setSelectedTicketType] = useState<TicketType | null>(null);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [issueTicketDialogOpen, setIssueTicketDialogOpen] = useState(false);

  // View mode state
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  useEffect(() => {
    loadProductions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProductions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await passkitAPI.getCompanyProductions();

      if (response.productions && response.productions.length > 0) {
        setProductions(response.productions);

        // Automatically select first production
        if (!selectedProduction) {
          setSelectedProduction(response.productions[0]);
          loadTicketTypesForProduction(response.productions[0]);
        }
      } else {
        setError('Nema dostupnih produkcija. Molimo prvo kreirajte produkciju u Resource Management.');
      }
    } catch (err: any) {
      logger.error('Error loading productions:', err);
      setError(err.response?.data?.detail || 'Greška pri učitavanju produkcija');
    } finally {
      setLoading(false);
    }
  };

  const loadTicketTypesForProduction = async (production: Production) => {
    try {
      setLoadingTicketTypes(true);
      setError(null);

      const response = await passkitAPI.getTicketTypeTemplates(production.id);

      if (response.templates) {
        // Map templates to ticket types with production name
        const typesWithProduction = response.templates.map((template: any) => ({
          ...template,
          production_name: production.name
        }));
        setTicketTypes(typesWithProduction);
      } else {
        setTicketTypes([]);
      }

      // Load events from response
      if (response.events) {
        logger.log('📅 Loaded events from API:', response.events);
        setEvents(response.events);
      } else {
        setEvents([]);
      }
    } catch (err: any) {
      logger.error('Error loading ticket types:', err);
      // Don't show error if production has no ticket types yet
      if (err.response?.status !== 404) {
        setError(err.response?.data?.detail || 'Greška pri učitavanju događaja');
      }
      setTicketTypes([]);
    } finally {
      setLoadingTicketTypes(false);
    }
  };

  const handleProductionChange = (production: Production) => {
    setSelectedProduction(production);
    loadTicketTypesForProduction(production);
  };

  const handleCreateSuccess = () => {
    setSuccessMessage('Događaj je uspešno kreiran!');
    setTimeout(() => setSuccessMessage(null), 5000);

    // Reload ticket types for selected production
    if (selectedProduction) {
      loadTicketTypesForProduction(selectedProduction);
    }
  };

  const handleEditTicketType = (ticketType: TicketType) => {
    setSelectedTicketType(ticketType);
    setEditDialogOpen(true);
  };

  const handleDeleteTicketType = (ticketType: TicketType) => {
    setSelectedTicketType(ticketType);
    setDeleteDialogOpen(true);
  };

  const handleEditSuccess = () => {
    setSuccessMessage('Templejt je uspešno izmenjen!');
    setTimeout(() => setSuccessMessage(null), 5000);

    // Reload ticket types for selected production
    if (selectedProduction) {
      loadTicketTypesForProduction(selectedProduction);
    }
  };

  const handleDeleteSuccess = () => {
    setSuccessMessage('Templejt je uspešno obrisan!');
    setTimeout(() => setSuccessMessage(null), 5000);

    // Reload ticket types for selected production
    if (selectedProduction) {
      loadTicketTypesForProduction(selectedProduction);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <ResponsivePageHeader
        title="Događaji"
        subtitle="Upravljajte templejtima i kalendarom događaja"
        icon={<TicketIcon sx={{ fontSize: 40 }} />}
        actions={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setTicketTypeDialogOpen(true)}
              disabled={!selectedProduction || loading}
            >
              Novi Templejt
            </Button>
            <Button
              variant="outlined"
              startIcon={<TicketIcon />}
              onClick={() => setIssueTicketDialogOpen(true)}
              disabled={!selectedProduction || loading || ticketTypes.length === 0}
            >
              Kreiraj Kartu
            </Button>
          </Box>
        }
      />

      {/* Tabs for switching between Templates and Calendar */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Templejti"
            value="list"
            icon={<TicketIcon />}
            iconPosition="start"
          />
          <Tab
            label="Kalendar"
            value="calendar"
            icon={<CalendarIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Production Selector */}
      {!loading && productions.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Izaberi Produkciju
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {productions.map((production) => (
              <Chip
                key={production.id}
                label={production.name}
                onClick={() => handleProductionChange(production)}
                color={selectedProduction?.id === production.id ? 'primary' : 'default'}
                variant={selectedProduction?.id === production.id ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
          {selectedProduction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Izabrana produkcija:</strong> {selectedProduction.name}
              </Typography>
              {selectedProduction.description && (
                <Typography variant="body2" color="text.secondary">
                  {selectedProduction.description}
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Conditional View Rendering */}
          {selectedProduction && (
            <>
              {viewMode === 'list' ? (
                /* Events Table */
                <TableContainer component={Paper}>
                  {loadingTicketTypes ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Naziv</strong></TableCell>
                          <TableCell><strong>Terms & Conditions</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Datum kreiranja</strong></TableCell>
                          <TableCell align="right"><strong>Akcije</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ticketTypes.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Box sx={{ py: 3 }}>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                  Nema kreiranih templejta za ovu produkciju.
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Kliknite na "Novi Događaj" da kreirate prvi.
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          ticketTypes.map((ticketType) => (
                            <TableRow key={ticketType.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TicketIcon sx={{ color: 'text.secondary' }} />
                                  <Box>
                                    <Typography variant="body1" fontWeight="medium">
                                      {ticketType.name}
                                    </Typography>
                                    {ticketType.id && (
                                      <Typography variant="caption" color="text.secondary">
                                        ID: {ticketType.id}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {ticketType.description || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={ticketType.status || 'UNKNOWN'}
                                  size="small"
                                  color={ticketType.status === 'PUBLISHED' ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {ticketType.created_at ? new Date(ticketType.created_at).toLocaleDateString('sr-RS') : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Uredi Templejt">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditTicketType(ticketType)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Obriši Templejt">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteTicketType(ticketType)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              ) : (
                /* Calendar View */
                <Paper sx={{ p: 2 }}>
                  {loadingTicketTypes ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : (
                    <EventCalendarView
                      events={events.map(event => ({
                        id: event.id,
                        name: event.name || 'Događaj',
                        date: event.event_date,
                        venue: event.venue_name,
                        venue_id: event.venue_id
                      }))}
                      onAddEvent={(date) => {
                        setSelectedDate(date);
                        setAddEventDialogOpen(true);
                      }}
                      onEventClick={(event) => {
                        logger.log('Event clicked:', event);
                      }}
                      onEventUpdated={() => {
                        if (selectedProduction) {
                          loadTicketTypesForProduction(selectedProduction);
                        }
                      }}
                      productionId={selectedProduction?.passkit_production_id || ''}
                    />
                  )}
                </Paper>
              )}
            </>
          )}
        </>
      )}

      {/* Create Event Dialog */}
      {selectedProduction && (
        <EventCreationDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          productionId={selectedProduction.id}
          productionName={selectedProduction.name}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Create Template Dialog */}
      {selectedProduction && (
        <TicketTypeCreationDialog
          open={ticketTypeDialogOpen}
          onClose={() => setTicketTypeDialogOpen(false)}
          productionId={selectedProduction.id}
          productionName={selectedProduction.name}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Edit Template Dialog */}
      <TicketTypeEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTicketType(null);
        }}
        ticketType={selectedTicketType}
        productionId={selectedProduction?.id || ''}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Template Dialog */}
      <TicketTypeDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedTicketType(null);
        }}
        ticketType={selectedTicketType}
        productionId={selectedProduction?.id || ''}
        onSuccess={handleDeleteSuccess}
      />

      {/* Add Event Dialog (from calendar) */}
      {selectedProduction && (
        <AddEventDialog
          open={addEventDialogOpen}
          onClose={() => {
            setAddEventDialogOpen(false);
            setSelectedDate(null);
          }}
          productionId={selectedProduction.id}
          preselectedDate={selectedDate}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Issue Ticket Dialog */}
      {selectedProduction && (
        <IssueTicketDialog
          open={issueTicketDialogOpen}
          onClose={() => setIssueTicketDialogOpen(false)}
          productionId={selectedProduction.passkit_production_id}
          venueId={selectedProduction.id}
          events={events}
          ticketTypes={ticketTypes}
        />
      )}
    </Box>
  );
};

export default Events;
